<script setup lang="ts">
const { loggedIn } = useUserSession();
const route = useRoute();
const navType = computed(() => loggedIn.value ? 'website' : 'auth');

const docsNavigation = [
	[
		{
			label: 'What is CSP?',
			icon: 'i-lucide-shield-check',
			to: '/docs'
		},
		{
			label: 'How It Works',
			icon: 'i-lucide-cog',
			to: '/docs/how-it-works'
		}
	]
];

const sidebarOpen = ref(false);
const sidebarCollapsed = ref(false);

const toggleSidebar = () => { sidebarOpen.value = !sidebarOpen.value };
const toggleSidebarCollapse = () => { sidebarCollapsed.value = !sidebarCollapsed.value };
const sidebarNotCollapsed = () => { sidebarCollapsed.value = false };

provide('sidebarToggle', toggleSidebar);
provide('sidebarCollapse', toggleSidebarCollapse);
provide('sidebarNotCollapsed', sidebarNotCollapsed);
</script>

<template>
	<Navbar :nav-type="navType" :show-sidebar-toggle="true" />
	<div class="flex h-full min-h-screen items-stretch py-[var(--ui-header-height)]" v-if="!loggedIn">
		<!-- Mobile sidebar drawer for non-logged-in users -->
		<UDrawer direction="left" :handle="false" inset v-model:open="sidebarOpen" :dismissible="true" class="md:hidden">
			<template #content>
				<div class="p-4 bg-background h-full">
					<div class="space-y-4">
						<div class="flex items-center justify-between">
							<h2 class="font-semibold text-lg">Documentation</h2>
							<UButton icon="i-lucide-x" variant="ghost" size="sm" @click="sidebarOpen = false" />
						</div>
						<UNavigationMenu class="overflow-y-auto" orientation="vertical" variant="link" :items="docsNavigation"
							@click="() => { sidebarNotCollapsed(); sidebarOpen = false; }" />
					</div>
				</div>
			</template>
		</UDrawer>

		<!-- Desktop sidebar for non-logged-in users -->
		<div class="bg-elevated shadow-lg border-r border-border hidden md:block transition-all duration-300 ease-in-out"
			:class="{ 'w-64': !sidebarCollapsed, 'w-14': sidebarCollapsed }">
			<div class="p-4 sticky top-[var(--ui-header-height)] h-[calc(100vh-var(--ui-header-height))] flex flex-col">
				<div class="flex items-center justify-between mb-6">
					<h2 v-if="!sidebarCollapsed" class="font-semibold text-lg">Documentation</h2>
					<div class="flex-shrink-0" :class="{ 'mx-auto': sidebarCollapsed }">
						<DesktopSidebarToggle />
					</div>
				</div>
				<UNavigationMenu class="overflow-y-auto flex-1" :collapsed="sidebarCollapsed" orientation="vertical"
					variant="link" :items="docsNavigation" @click="sidebarNotCollapsed" />
			</div>
		</div>

		<div class="flex-1 min-w-0">
			<slot />
		</div>
	</div>

	<!-- For logged-in users, just render content without sidebar -->
	<div class="flex h-full min-h-screen items-stretch py-[var(--ui-header-height)]" v-else>
		<UDrawer direction="left" :handle="false" inset v-model:open="sidebarOpen" :dismissible="true" class="md:hidden">
			<template #content>
				<AppSidebar :collapsed="false" />
			</template>
		</UDrawer>
		<div class="shadow-xl z-40 hidden md:block transition-all duration-300 ease-in-out" v-if="loggedIn"
			:class="{ 'w-64': !sidebarCollapsed, 'w-14': sidebarCollapsed }">
			<AppSidebar :collapsed="sidebarCollapsed" />
		</div>
		<div class="flex-1">
			<EmailVerificationNotification />
			<SubscriptionNotification />
			<slot />
		</div>
	</div>
	<Footer />
</template>
